import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UserIcon } from '@heroicons/react/outline';
import { getPreferredTranslation } from '@zedoc/i18n';
import compact from 'lodash/compact';
import uniq from 'lodash/uniq';
import { isoLangs } from '@zedoc/iso-langs';
import { getLanguageDisplayName } from '../../utils/languageDisplayName';
import { apiZedocListTranslationOptionsForAnswersSheet } from '../../common/api/zedoc';
import Select from '../Select';
import useList from '../../utils/useList';

const TranslationsSelect = ({
  type,
  answersSheetId,
  answersSheetLanguages,
  value,
  onChange,
  valueAsLanguage,
  items: externalItems,
  enabled = true,
}) => {
  const { i18n } = useTranslation();
  const uiLanguage = i18n.language;

  const { items: subscribedItems } = useList(
    enabled && !externalItems && answersSheetId
      ? apiZedocListTranslationOptionsForAnswersSheet.withParams({
          answersSheetId,
          resultId: '$meta.id',
          uiLanguage,
        })
      : null,
  );

  const items = externalItems || subscribedItems;

  const originalQuestionnaireLanguage = useMemo(() => {
    return items.find(({ version }) => version === '0');
  }, [items]);

  const translations = items;

  const languagePreference = useMemo(
    () =>
      uniq(
        compact([
          i18n.resolvedLanguage,
          i18n.language,
          i18n.language?.split('-')[0],
        ]),
      ),
    [i18n.resolvedLanguage, i18n.language],
  );

  const defaultTranslation = useMemo(() => {
    return (
      getPreferredTranslation(languagePreference, translations) ||
      originalQuestionnaireLanguage
    );
  }, [languagePreference, translations, originalQuestionnaireLanguage]);

  const translationThatMatchesUILanguage = useMemo(
    () => translations.find(({ language }) => language === uiLanguage),
    [translations, uiLanguage],
  );

  const languageOptions = useMemo(() => {
    const showPatientLanguageIcon = answersSheetLanguages.length === 1;

    const generateTranslations = ({ language, translationId }) => {
      // Shown in the UI language (TC48); the test id keeps the stable English
      // name the Cypress specs select by.
      const languageName = getLanguageDisplayName(language, uiLanguage);
      const englishName = isoLangs[language]?.name;
      const isAnswersSheetLanguage =
        showPatientLanguageIcon && answersSheetLanguages.includes(language);

      return {
        value: valueAsLanguage ? language : translationId,
        optionTestId: englishName,
        label: isAnswersSheetLanguage ? (
          <span className="cluster-2 items-center">
            <span>{languageName}</span>
            <UserIcon className="w-4 h-4 flex-shrink-0 text-secondary" />
          </span>
        ) : (
          languageName
        ),
      };
    };

    if (!translationThatMatchesUILanguage && originalQuestionnaireLanguage) {
      const translationThatMatchesBaseQuestionnaireLanguage = translations.some(
        (translation) =>
          translation.language === originalQuestionnaireLanguage.language,
      );

      // NOTE: If no translation that matches UI language is present than use questionnaire language instead
      if (!translationThatMatchesBaseQuestionnaireLanguage) {
        return [originalQuestionnaireLanguage, ...translations].map(
          generateTranslations,
        );
      }
    }

    return translations.map(generateTranslations);
  }, [
    translations,
    translationThatMatchesUILanguage,
    originalQuestionnaireLanguage,
    valueAsLanguage,
    answersSheetLanguages,
    uiLanguage,
  ]);

  const handleCurrentLanguageChange = (language) => onChange(language);
  useEffect(() => {
    if (!defaultTranslation) {
      return;
    }

    const defaultValue = valueAsLanguage
      ? defaultTranslation.language
      : defaultTranslation.translationId;
    const valueInOptions = languageOptions.some(
      (option) => option.value === value,
    );

    if (value == null || !valueInOptions) {
      onChange(defaultValue);
    }
  }, [onChange, defaultTranslation, valueAsLanguage, value, languageOptions]);

  return languageOptions.length > 1 ? (
    <Select
      type={type}
      options={languageOptions}
      onChange={handleCurrentLanguageChange}
      value={value}
    />
  ) : null;
};

TranslationsSelect.propTypes = {
  type: PropTypes.oneOf(['primary', 'secondary']),
  answersSheetId: PropTypes.string,
  answersSheetLanguages: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
  onChange: PropTypes.func,
  valueAsLanguage: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.object),
  enabled: PropTypes.bool,
};

TranslationsSelect.defaultProps = {
  type: 'secondary',
  answersSheetId: null,
  answersSheetLanguages: [],
  value: null,
  onChange: () => {},
  valueAsLanguage: false,
  items: null,
  enabled: true,
};

export default TranslationsSelect;
